/**
 * Multi-select tag editor.
 *
 * Used in create/edit forms (Workflow, Ingress, Canonical). Behaviour:
 *   - displays selected tags as chips with an X to remove
 *   - typing in the input filters the autocomplete list of in-use tags
 *   - Enter (or click on a suggestion) commits the typed/highlighted tag
 *   - submitting a tag not yet in the catalog is allowed (free-form)
 *   - duplicate / whitespace-only / over-length entries are silently dropped
 *
 * Built on Radix Popover for proper Dialog interop.
 */
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Plus, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { tagsApi } from "@/lib/api-tags";
import { cn } from "@/lib/cn";

const MAX_TAG_LEN = 64;

export interface TagsFieldProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Restrict the autocomplete pool to tags already used on a given
   *  resource type. When unset, all in-use tags are suggested. */
  scope?: "workflows" | "ingress_sources" | "canonicals";
}

function normalize(raw: string): string | null {
  const cleaned = raw.trim().toLowerCase();
  if (!cleaned) return null;
  if (cleaned.length > MAX_TAG_LEN) return null;
  return cleaned;
}

export function TagsField({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  id,
  scope,
}: TagsFieldProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [highlight, setHighlight] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const tagsQ = useQuery({
    queryKey: ["tags-catalog"],
    queryFn: tagsApi.list,
    staleTime: 30_000,
  });

  const pool = React.useMemo(() => {
    const items = tagsQ.data?.tags ?? [];
    if (!scope) return items.map((u) => u.tag);
    return items.filter((u) => u[scope] > 0).map((u) => u.tag);
  }, [tagsQ.data, scope]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const remaining = pool.filter((tag) => !value.includes(tag));
    if (!q) return remaining;
    return remaining.filter((tag) => tag.includes(q));
  }, [pool, query, value]);

  React.useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  function commit(raw: string) {
    const next = normalize(raw);
    if (!next) return;
    if (value.includes(next)) return;
    onChange([...value, next]);
    setQuery("");
    // Re-focus so the user can keep adding without re-clicking.
    inputRef.current?.focus();
  }

  function remove(tag: string) {
    onChange(value.filter((t) => t !== tag));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (filtered[highlight]) {
        commit(filtered[highlight]);
      } else if (query.trim()) {
        commit(query);
      }
    } else if (e.key === "Backspace" && query === "" && value.length > 0) {
      // Quick-remove last chip when input is empty (like Gmail recipients).
      remove(value[value.length - 1]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          "flex min-h-8 w-full flex-wrap items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-sm",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
        onClick={() => {
          if (!disabled) {
            inputRef.current?.focus();
            setOpen(true);
          }
        }}
        id={id}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex h-5 items-center gap-1 rounded-full bg-muted pl-2 pr-1 font-mono text-[11px] text-muted-foreground"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(tag);
                }}
                className="rounded-full p-0.5 hover:bg-foreground/10"
                aria-label={t("common.cancel")}
              >
                <X className="h-2.5 w-2.5" strokeWidth={1.5} />
              </button>
            )}
          </span>
        ))}

        <PopoverPrimitive.Anchor asChild>
          <input
            ref={inputRef}
            type="text"
            value={query}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!open) setOpen(true);
            }}
            onKeyDown={onKeyDown}
            placeholder={
              value.length === 0
                ? placeholder ?? t("tags.placeholder", "Add tags…")
                : ""
            }
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
            className="min-w-[8ch] flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
          />
        </PopoverPrimitive.Anchor>

        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </div>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          sideOffset={4}
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            "z-50 max-h-64 w-[var(--radix-popover-trigger-width)] overflow-y-auto rounded-md border border-border bg-elevated text-sm shadow-lg",
          )}
        >
          {tagsQ.isLoading && (
            <div className="px-3 py-1.5 text-xs text-muted-foreground">
              {t("common.loading")}
            </div>
          )}
          {!tagsQ.isLoading && filtered.length === 0 && !query.trim() && (
            <div className="px-3 py-1.5 text-xs text-muted-foreground">
              {t("tags.noSuggestions", "No suggestions")}
            </div>
          )}
          <ul role="listbox" className="py-1">
            {filtered.map((tag, i) => (
              <li
                key={tag}
                role="option"
                aria-selected={i === highlight}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => commit(tag)}
                className={cn(
                  "flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5",
                  "hover:bg-muted/70",
                  i === highlight && "bg-accent/15",
                )}
              >
                <span className="font-mono text-[12px]">{tag}</span>
              </li>
            ))}
            {query.trim() &&
              normalize(query) &&
              !value.includes(normalize(query)!) &&
              !filtered.includes(normalize(query)!) && (
                <li
                  role="option"
                  onClick={() => commit(query)}
                  className="flex cursor-pointer items-center gap-2 border-t border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                >
                  <Plus className="h-3 w-3" strokeWidth={1.5} />
                  {t("tags.createNew", "Create")}{" "}
                  <span className="font-mono text-foreground">
                    {normalize(query)}
                  </span>
                </li>
              )}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
